export type ExecutionResult =
  | {
      success: true;
      value: string | number | boolean;
      identifiers: {
        lexeme: string;
        extents: { start: number; end: number };
      }[];
      extents: { start: number; end: number };
    }
  | {
      success: false;
      position: number;
      errors: {
        message: string;
        correction: ErrorCorrection;
      }[];
      missingFields: string[];
    };

export function isExecutionResult(result: ExecutionResult): result is ExecutionResult {
  return typeof result === 'object' && 'success' in result;
}

export type BuiltIn = {
  arity?: number;
  implementation: (
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    args: any[],
    extents: { start: number; end: number },
    inputArgs: { lexeme: string; extents: { start: number; end: number } }[],
  ) => ExecutionResult;
};

export const TokenTypes = {
  ID: 'ID',
  EQUALS: 'EQUALS',
  COMPOP: 'COMPOP',
  MULDIVOP: 'MULDIVOP',
  ADDSUBOP: 'ADDSUBOP',
  OPEN_PARENS: 'OPEN_PARENS',
  CLOSE_PARENS: 'CLOSE_PARENS',
  COMMA: 'COMMA',
} as const;

export type Token = (typeof TokenTypes)[keyof typeof TokenTypes];

export type NtTree = Terms.Value | Terms.Application | Terms.BinaryExpression | Terms.Group;
export type TweObj = { token: Token; lexeme: string; start: number; end: number };
export type Descriptor = {
  parent: Descriptor | null;
  production: ({ nt: string } | { t: Token })[];
  process: (children: (NtTree | TweObj)[]) => NtTree;
  position: number;
  subtrees: (NtTree | TweObj)[];
  errors: {
    message: string;
    correction: ErrorCorrection;
  }[];
};

export type ParseResult = {
  success: boolean;
  tree: Terms.Value | Terms.Application | Terms.BinaryExpression;
  position: number;
  errors: {
    message: string;
    correction:
      | { insert: string; position: number }
      | { deleteFrom: number; deleteTo: number }
      | { replaceFrom: number; replaceTo: number; replaceWith: string };
  }[];
};

export function IsBinaryExpression(
  term: Terms.Value | Terms.Application | Terms.BinaryExpression,
): term is Terms.BinaryExpression {
  return !!(term && typeof term === 'object' && 'binOp' in term);
}

export function IsApplication(
  term: Terms.Value | Terms.Application | Terms.BinaryExpression,
): term is Terms.Application {
  return !!(term && typeof term === 'object' && 'funcId' in term);
}

// eslint-disable-next-line @typescript-eslint/no-namespace
export namespace Terms {
  export type BinaryExpression = {
    extents: { start: number; end: number };
    leftExpr: BinaryExpression | Application | Value;
    binOp: string;
    rightExpr: BinaryExpression | Application | Value;
  };

  export type Application = {
    extents: { start: number; end: number };
    funcId: string;
    arguments: (Value | Application | BinaryExpression)[];
  };

  export type Value = {
    extents: { start: number; end: number };
    value: string;
  };

  export type Group = {
    extents: { start: number; end: number };
    values: (BinaryExpression | Application | Value | Group | TweObj)[];
  };
}

export type ErrorCorrection =
  | { insert: string; position: number }
  | { deleteFrom: number; deleteTo: number }
  | { replaceFrom: number; replaceTo: number; replaceWith: string };
